import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Clock4 } from 'lucide-react'

import { apiErrorMessage, apiFetch } from '@/lib/api'
import {
  SESSION_QUERY_KEY,
  dropUserScopedQueries,
  landingFor,
  useSession,
  type SessionUser,
} from '@/lib/auth'
import Alert from '@/components/ui/Alert'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import Label from '@/components/ui/Label'

const schema = z.object({
  email: z
    .string()
    .min(1, 'Wpisz email')
    .email('Nieprawidłowy format email'),
  password: z.string().min(1, 'Wpisz hasło'),
})

type FormValues = z.infer<typeof schema>

export default function Login() {
  const session = useSession()
  const navigate = useNavigate()
  const location = useLocation()
  const qc = useQueryClient()

  const params = new URLSearchParams(location.search)
  const resetOk = params.get('reset') === 'ok'
  const expired = params.get('expired') === '1'

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  })

  // Już zalogowany → idź na landing wg roli/flagi
  useEffect(() => {
    if (session.data) {
      navigate(landingFor(session.data), { replace: true })
    }
  }, [session.data, navigate])

  const login = useMutation({
    mutationFn: (values: FormValues) =>
      apiFetch<{ user: SessionUser }>('/api/auth/login', {
        method: 'POST',
        body: values,
      }),
    onSuccess: ({ user }) => {
      // Sesja wygasła bez wylogowania, więc cache może trzymać stan pracy
      // poprzedniej osoby — nowy użytkownik zaczyna od czystych danych.
      dropUserScopedQueries(qc)
      qc.setQueryData(SESSION_QUERY_KEY, user)
      navigate(landingFor(user), { replace: true })
    },
  })

  const submitError = login.error ? apiErrorMessage(login.error) : null

  return (
    <main className="min-h-[100svh] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-[420px] rounded-card border border-border-subtle bg-bg-elevated p-8">
        <div className="mb-6 flex flex-col items-center text-center">
          <Clock4 className="mb-2 h-6 w-6 text-brand-accent" aria-hidden />
          <p className="text-xs uppercase tracking-widest text-text-faint">
            Dental Avenue
          </p>
          <h1 className="mt-1 text-2xl font-semibold">Czas pracy</h1>
        </div>

        {resetOk && (
          <div className="mb-4">
            <Alert variant="success">Hasło zmienione. Zaloguj się nowym.</Alert>
          </div>
        )}
        {expired && (
          <div className="mb-4">
            <Alert variant="info">
              Link do resetu wygasł. Wygeneruj nowy poniżej.
            </Alert>
          </div>
        )}

        <form
          onSubmit={form.handleSubmit((v) => login.mutate(v))}
          noValidate
          className="space-y-4"
        >
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              autoFocus
              invalid={!!form.formState.errors.email}
              {...form.register('email')}
            />
            {form.formState.errors.email && (
              <p className="mt-1 text-xs text-danger">
                {form.formState.errors.email.message}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="password">Hasło</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              invalid={!!form.formState.errors.password}
              {...form.register('password')}
            />
            {form.formState.errors.password && (
              <p className="mt-1 text-xs text-danger">
                {form.formState.errors.password.message}
              </p>
            )}
          </div>

          {submitError && <Alert variant="error">{submitError}</Alert>}

          <Button type="submit" loading={login.isPending} className="w-full">
            Zaloguj się
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/forgot-password"
            className="text-sm text-text-muted hover:text-text-primary transition"
          >
            Nie pamiętam hasła
          </Link>
        </div>
      </div>
    </main>
  )
}
